import { Schema } from "effect";
import { AuditEvent } from "./audit.ts";
import {
  AgentDraftDecisionPayload,
  ChangeSetJson,
  CollabVersion,
  CommitLandedPayload,
  DiagnosticsPayload,
  DraftDiscardPayload,
  DraftId,
  DraftOpenPayload,
  DraftStatePayload,
  DraftSubmitPayload,
  DraftUpdatePayload,
  LandRequestPayload,
  MyDraftsPayload,
  OffsetRegion,
  RestoreVersionPayload,
  SnapshotCollabFields,
} from "./collab.ts";
import { DocumentId } from "./ids.ts";
import { Position, ViewerPresence } from "./presence.ts";
import { ReviewStateWire } from "./review-state.ts";

// The WebSocket live-sync wire contract (N3/N4) as boundary `Schema`s shared by the server
// (encodes) and the browser (decodes). The browser no longer receives patches-by-version: it
// receives the **audit events themselves** by `seq` (ADR-0011), the same events the log stores, and
// folds them into `(text, reviewState)` exactly as the server-side projection does. One stream, one
// fold, no second representation to drift.
//
// The opening `Snapshot` ships the head document bytes verbatim plus the folded `reviewState` and
// the two cursors that address them — `version` (the `docVersion` the text is at) and `seq` (the log
// position the review state is folded to). Each subsequent `Event` carries one `AuditEvent` and its
// `seq`: a doc-modifying event carries a `patch` the client applies (and a `docVersion` it advances
// to); a review-only event (keep, reclassify, dispose) carries neither and only moves `reviewState`.
// The client tracks the last `seq` it folded; a gap (an `Event` whose `seq` is not its cursor + 1)
// means it missed frames and must resync from a fresh snapshot.
//
// This subsumes the retired `Patch` (a byte event now *is* an `Event` carrying its `patch`) and
// `Resolved` (a terminal byte-no-op decision is just an `Event` with no `patch`). `core` types the
// patch as an opaque string and never imports `diff-match-patch-ts`; `patch_make`/`patch_apply` live
// in `apps/server` (the broadcast) and `apps/web` (the client).
//
// DEPRECATION (ADR-0043 / ADR-0004 amend): The DMP patch-chain model types below
// (Snapshot, Event, ServerMessage as currently defined) are DEPRECATED in favour of the
// ChangeSet-based collab model. They remain because server and web code still consume them
// until U3 (server landing seam) and U9 (web CM host) land. Do not add new callers.
// Retirement unit: U3 (server) / U9 (web).

const Version = Schema.Int.pipe(Schema.nonNegative());

export const Snapshot = Schema.TaggedStruct("snapshot", {
  documentId: DocumentId,
  version: Version,
  seq: Version,
  document: Schema.String,
  reviewState: ReviewStateWire,
  // WHO THIS CONNECTION IS — the one frame that tells a browser about itself (CON-579). `Presence`
  // deliberately excludes the receiving viewer's own connections (ADR-0013: a viewer never sees their
  // own avatar in live awareness), so before this field the client had no way to learn its own display
  // identity short of opening a draft and reading the `EditorIdentity` off the reply. The rail's
  // "signed in as" avatar needs it with no draft and no second round trip, and the handshake has
  // already resolved it. Same derived display identity `Presence` carries and nothing more — a
  // `ConnectionId`, a name and a colour, never a `ViewerId`.
  viewer: ViewerPresence,
  // "pending" while a `generating`-origin genesis (KTD1) has no terminal event yet, "failed" once its
  // run's `document.generation-failed` lands, absent otherwise (an ordinary document, or one whose run
  // resolved with `document.generation-completed`) — mirrors `CurriculumListItem.generationStatus`
  // (tool-args.ts) so the list row and the document page read the same fact off the same fold (U8).
  generationStatus: Schema.optional(Schema.Literal("pending", "failed")),
  // The `document.generation-failed` event's own reason, present exactly when `generationStatus`
  // is `"failed"`. Without this a viewer who reconnects/reloads after the failure — rather than
  // watching the live `generation-failed` frame — has no way to learn why: the snapshot is the only
  // frame they get.
  failureReason: Schema.optional(Schema.String),
  // The bootstrap peel (U5, plan "Undo, optimism, and bootstrap"): collabVersion + every open draft's
  // full state, keyed to the SAME head read as `version`/`seq` above — no window where a client has
  // text but not the version, or drafts keyed to a version it never saw. See `SnapshotCollabFields` in
  // collab.ts. Diagnostics were part of this atomic peel until the WS first-message latency fix
  // (ADR-0045's dated amendment): computing them was blocking on `projectDiagnostics`/
  // `Curriculum.lint`/`Curriculum.audit` before any content frame could ship. They now arrive
  // asynchronously on their own `DiagnosticsPayload` ("diagnostics") frame once `DiagnosticsService`
  // finishes computing them — never blocking `Snapshot`.
  ...SnapshotCollabFields.fields,
});
export type Snapshot = typeof Snapshot.Type;

// One audit event with the storage `seq` the append seam assigned it. The client folds it into text
// (when it carries a `patch`) and review state, then advances its cursor to this `seq`.
export const Event = Schema.TaggedStruct("event", {
  seq: Version,
  event: AuditEvent,
});
export type Event = typeof Event.Type;

// A server-driven review run's status, broadcast alongside the document stream. It carries **no**
// `version`/`seq`: it is orthogonal to the document text and review state and must never advance or
// gate either cursor. `kind` names the run in flight (omitted when `running` is false) — `checks` is
// the only startable kind (R2: the `judge`/`refine` runs are retired, and this status is never
// persisted, so there is no historical value to keep decoding). The client uses it only to drive the
// shared run spinner — it never touches `text`/`reviewState`.
export const ReviewStatus = Schema.TaggedStruct("review-status", {
  documentId: DocumentId,
  running: Schema.Boolean,
  kind: Schema.optional(Schema.Literal("checks")),
});
export type ReviewStatus = typeof ReviewStatus.Type;

// Exercise-expander run progress, broadcast to EVERY viewer of a document rather than polled by the
// browser that started the run. Like `ReviewStatus` it is orthogonal to text/review state, carries no
// `version`/`seq`, and never advances either cursor; and like `diagnostics` each frame is an
// idempotent snapshot, so a dropped one is healed by the next.
//
// `done`/`total` count authored MODULES for the whole run. The step-4 dialog-polish pass — which
// dominates a long run's wall-clock — reports separately through `polishDone`/`polishTotal` and names
// its pass (`diagnose`, then `rewrite`); those reset per pass. `scopeTargets` names the modules the
// run is actually working on, so a viewer who did not start it can tell a one-module expand from a
// many-module run. `partialFailure`/`failedModules` are the only signal that a finished run was
// incomplete.
//
// The check suite has no equivalent progress frame at all (CON-737 U6: the run matrix and its live-cell
// broadcast were deleted, not reworked — a document's quality readout is now a `FindingSummary` count,
// folded from the same `diagnostics`/`snapshot` frames below, with nothing left to stream per-cell).
// Expand progress rides the WS on its own merits: the sidecar batches to one frame per `--batch` window
// and the controller coalesces again on `EXPAND_FOLD_INTERVAL`, so this is a trickle with nothing to
// replay, and the run's own diff already rides this socket as `draft-state`. Reuse beat a second
// fan-out path here.
// One run's own status (KTD5, single-flight PER SCOPE, ADR-0061 amendment 2026-09-15): `core` cannot
// import `@curriculum-studio/ports` (dependencies point inward only), so this mirrors the port's
// `ExpandStatus` interface field-for-field as the wire shape, kept under the same name so the port
// and the wire agree on vocabulary even though they cannot share a definition.
export const ExpandStatus = Schema.Struct({
  running: Schema.Boolean,
  done: Schema.Number,
  total: Schema.Number,
  draftId: Schema.optional(DraftId),
  phase: Schema.optional(Schema.Literal("authoring", "polishing")),
  polishDone: Schema.Number,
  polishTotal: Schema.Number,
  polishPass: Schema.optional(Schema.String),
  scopeTargets: Schema.Array(Schema.String),
  startedAt: Schema.optional(Schema.Number),
  runId: Schema.optional(Schema.String),
  partialFailure: Schema.optional(Schema.Boolean),
  failedModules: Schema.optional(Schema.Array(Schema.String)),
});
export type ExpandStatus = typeof ExpandStatus.Type;

// KTD8 (prune-on-read): the full set of running-or-pending-review entries for the document, replaced
// WHOLESALE on every frame (never a per-run upsert/remove delta) — a run stays in `runs` with
// `running: false` until its draft resolves (`landed`/`discarded`), then is pruned server-side on the
// next read; a `stale` draft is never pruned. Two disjoint-scope runs on the same document both appear.
export const ExpandProgress = Schema.TaggedStruct("expand-progress", {
  documentId: DocumentId,
  runs: Schema.Array(ExpandStatus),
});
export type ExpandProgress = typeof ExpandProgress.Type;

// A fix-dispatch turn's running status, broadcast to EVERY viewer the same way `ExpandStatus` is
// (CON-1315 unit 1: reactivity + all-viewer visibility for `[Fix]`/`[Fix all]`). `runId` is carried
// as a plain string (the wire cannot depend on the server's `JobRunId` brand) — not consumed by this
// unit's own client code, but needed by the upcoming cancel unit to target this exact run.
export const FixStatus = Schema.Struct({
  runId: Schema.String,
  ruleId: Schema.String,
  headingPath: Schema.optional(Schema.Array(Schema.String)),
  draftId: Schema.optional(DraftId),
});
export type FixStatus = typeof FixStatus.Type;

// Mirrors `ExpandProgress`'s prune-on-read broadcast shape but simpler: the array is ONLY ever the
// fix rows currently `lifecycle === "running"` for the document (see CON-1315 plan Finding 7 for why
// terminal rows are dropped rather than retained like `ExpandProgress` does) — presence in `runs` IS
// the "running" signal, no separate boolean needed.
export const FixProgress = Schema.TaggedStruct("fix-progress", {
  documentId: DocumentId,
  runs: Schema.Array(FixStatus),
});
export type FixProgress = typeof FixProgress.Type;

// Live awareness, broadcast alongside the document stream (ADR-0013). Like `ReviewStatus` it carries
// **no** `version`/`seq` — it is orthogonal to the document/review cursors and must never advance
// either. `viewers` is the full set for the document (snapshot semantics): a late subscriber receives
// the current set immediately and each subsequent `Presence` frame replaces the prior set. It carries
// only the derived display identity keyed by `ConnectionId` — never a `ViewerId`.
export const Presence = Schema.TaggedStruct("presence", {
  documentId: DocumentId,
  viewers: Schema.Array(ViewerPresence),
});
export type Presence = typeof Presence.Type;

// A mutating client push the server declined, reported to THAT SENDER ALONE (never fanned out — no
// other viewer's state changed, and the refusal names the sender's own draft).
//
// Why this frame exists. Before it, `ws.ts`'s inbound catch-all turned every refusal into a
// server-side `logWarning` and returned void, so a declined `draft-open`/`land-request` was
// indistinguishable on the wire from a frame that was still being processed. `draft-session.ts` sets
// its phase optimistically on send (`opening` on fork) and leaves it only on a reply, so a swallowed
// refusal hung the editor permanently — the staging symptom was two curricula pinned at `DRAFT_LIMIT`
// where every fork refused `DraftLimitExceeded` and no author was ever told.
//
// `reason` is the refusing error's `_tag`, NOT prose: the client recovers by tag (`Effect.catchTag`
// posture — see the non-negotiables in CLAUDE.md), and `detail` carries the human sentence for the
// rail. `operation` is the refused `ClientMessage` tag, so a client with several pushes in flight can
// tell which one died. A DECODE failure is deliberately not reported here — an unparseable frame has
// no trustworthy tag to attribute, so it stays log-only as it was.
export const OperationRefused = Schema.TaggedStruct("operation-refused", {
  documentId: DocumentId,
  operation: Schema.String,
  reason: Schema.String,
  detail: Schema.String,
  // Absent for a refusal that names no draft (a `draft-open` at the cap never minted one).
  draftId: Schema.optional(DraftId),
});
export type OperationRefused = typeof OperationRefused.Type;

// KTD11: mirrors `commit-landed`, fanned out to every viewer of a document the same way — the
// terminal outcome of a `generating`-origin genesis's async run (KTD1/KTD3). Defined directly here
// rather than in `collab.ts` (unlike `CommitLandedPayload`) since neither frame is collab-editing
// specific — both are plain terminal-event broadcasts a caller outside the draft/commit seam
// produces. Client-side handling lives in `useCollabBootstrap` (collab-document.tsx), not in
// `CollabDocumentSurfaceReady`'s WS switch — that surface never mounts while a document's
// generationStatus is "pending", so it would never observe these frames.
export const GenerationLandedPayload = Schema.TaggedStruct("generation-landed", {
  documentId: DocumentId,
  changeSetJson: ChangeSetJson,
  newVersion: CollabVersion,
});
export type GenerationLandedPayload = typeof GenerationLandedPayload.Type;

export const GenerationFailedPayload = Schema.TaggedStruct("generation-failed", {
  documentId: DocumentId,
  reason: Schema.String,
});
export type GenerationFailedPayload = typeof GenerationFailedPayload.Type;

// The terminal frame for a WS connection opened against a document the server has no audit history
// for (`broadcast.ts`'s `subscribe` failing `DocumentNotFound`). Before this frame existed the server
// just closed the socket with no explanation, which the client could not distinguish from an ordinary
// drop — it backed off and reconnected forever, and the page sat on its loading skeleton. The server
// sends exactly this one frame and ends the connection; the client must stop reconnecting on it
// (`ws-client.ts`'s `WsDocumentNotFound`) rather than treating the close as transient.
export const DocumentNotFoundPayload = Schema.TaggedStruct("document-not-found", {
  documentId: DocumentId,
});
export type DocumentNotFoundPayload = typeof DocumentNotFoundPayload.Type;

// CON-1186: the reply to a client `heartbeat` (below). Liveness proof for the CLIENT's watchdog
// (`ws-client.ts`) — a socket that never stops accepting writes but never replies either (a half-open
// dead write side) is exactly the failure mode `presence-hub.ts`'s own reaper already detects
// server-side (a stale connection it evicts); this frame gives the browser the same signal without
// waiting on the server-side TTL, and without overloading the WS close code (which cannot distinguish
// a drop from an auth denial — see `unauthenticated-page.tsx`'s module doc).
export const Pong = Schema.TaggedStruct("pong", {});
export type Pong = typeof Pong.Type;

// The WS document stream union. Check-suite frames are absent, full stop (CON-737 U6: the run matrix
// and its progress SSE were deleted, not moved elsewhere) — the WS carries only the document
// text/review/status/presence streams.
//
// U5 additions (ADR-0043/0044/0045, the coupled-landing pair with U2's payload schemas): `commit-landed`
// is the `receiveUpdates`-shaped feed of every landed ChangeSet (fans out to every connection on the
// document); `draft-state` is a draft snapshot/update broadcast (fans out on every draft mutation, already
// keystroke-batched by the client — the server does not re-batch); `diagnostics` is the version-keyed
// open-annotation push (fans out after every land and annotation event, U6). `ws.ts`'s dispatch and
// `broadcast.ts`'s stream handling land together with this union extension — see the plan's "Coupled
// landing constraint".
export const ServerMessage = Schema.Union(
  Snapshot,
  Event,
  ReviewStatus,
  Presence,
  CommitLandedPayload,
  DraftStatePayload,
  DiagnosticsPayload,
  MyDraftsPayload,
  ExpandProgress,
  FixProgress,
  OperationRefused,
  GenerationLandedPayload,
  GenerationFailedPayload,
  DocumentNotFoundPayload,
  Pong,
);
export type ServerMessage = typeof ServerMessage.Type;

// KTD12: the list-level frame for `GET /ws/curricula` — a SEPARATE socket from the per-document
// `ServerMessage` stream above (no snapshot, no drafts, no presence; just "something changed,
// go re-fetch"). Deliberately its own schema rather than a `ServerMessage` member: a per-document
// subscriber never receives this frame and a curricula-list subscriber never receives any other
// kind, so keeping them on separate wire unions makes that partition a type-level fact rather than
// a runtime convention.
export const CurriculumChanged = Schema.TaggedStruct("curriculum-changed", {
  documentId: DocumentId,
});
export type CurriculumChanged = typeof CurriculumChanged.Type;

// The inbound direction on the doc WS (ADR-0013 — the amendment to ADR-0004's one-way invariant).
// Originally **only ephemeral presence frames** could be sent by the browser. U5 (ADR-0043/0044)
// widens this: the socket now also carries client *pushes* — draft ops, land requests, and gate
// decisions — landed through the same DraftStore/commit-seam ports the MCP transport uses (the WS is
// a second transport over the same ports, never a second implementation). Keeping this a closed
// union handled with `Match.exhaustive` is the type-level backstop: adding any *mutating* inbound
// variant later is a deliberate, reviewable change to the union — it cannot slip in.

// "I am now viewing this section" — debounced on the client. `headingLine` is bounded non-negative at
// the decode boundary (see `Position`).
//
// U14 (co-viewer selections): `selection` rides the SAME frame, always in HEAD coordinates
// (mapped through the sender's own open draft's inverse first — see `presence-reporter.ts`). It
// is a full snapshot like `position`, not a delta: absent means "no selection right now" (a
// collapsed cursor), and the server-side hub applies it unconditionally on every frame — see
// `presence-hub.ts`'s `updateSelection`. Both fields are always reported together from the SAME
// debounced tick (`presence-reporter.ts`'s combined position+selection reporter) so there is never
// a second, independent producer that could clobber one field while only meaning to report the
// other.
export const PresenceUpdate = Schema.TaggedStruct("presence-update", {
  position: Position,
  selection: Schema.optional(OffsetRegion),
});
export type PresenceUpdate = typeof PresenceUpdate.Type;

// The idle-only liveness floor: an actively-scrolling viewer proves liveness through its
// `presence-update`s, so a bare `heartbeat` is emitted only by a quietly-reading viewer. Any inbound
// frame bumps the server's `lastSeen`, so the reaper needs no special-casing for the two kinds.
export const Heartbeat = Schema.TaggedStruct("heartbeat", {});
export type Heartbeat = typeof Heartbeat.Type;

export const ClientMessage = Schema.Union(
  PresenceUpdate,
  Heartbeat,
  DraftOpenPayload,
  DraftUpdatePayload,
  DraftSubmitPayload,
  DraftDiscardPayload,
  LandRequestPayload,
  AgentDraftDecisionPayload,
  // ADR-0112: roll a document forward onto an earlier version's content. A mutating frame (only
  // `presence-update`/`heartbeat` are ephemeral), so it is rate-budgeted like every other push.
  RestoreVersionPayload,
);
export type ClientMessage = typeof ClientMessage.Type;
